import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, RotateCcw, ArrowRight, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import {
  Button, Card, CardBody, Badge, EmptyState, Loading, Modal, Field, TextInput, Select,
} from "@/components/redvolt/kit";

const SOURCE_TYPES = [
  ["state_voter_file", "State voter file"], ["county_voter_file", "County voter file"],
  ["commercial_voter_file", "Commercial voter file"], ["committee_file", "Committee-maintained file"],
  ["campaign_file", "Campaign file"], ["generic_csv", "Generic CSV"],
];
const STATUS_LABEL = {
  uploaded: "Uploaded", mapping: "Mapping", validating: "Validating",
  ready_for_review: "Ready to review", committing: "Importing…", completed: "Complete",
  failed: "Failed", rolled_back: "Rolled back", cancelled: "Cancelled",
};
const STATUS_VARIANT = {
  completed: "implemented", ready_for_review: "active", committing: "vision",
  failed: "warning", rolled_back: "neutral", cancelled: "neutral",
};

export default function ImportsPage({ api, tenantId, caps }) {
  const canManage = caps.includes("import:manage");
  const canCommit = caps.includes("import:commit");
  const canRollback = caps.includes("import:rollback");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(null); // {stage, job, mapping, targets, summary, rows}

  const load = useCallback(async () => {
    setLoading(true);
    try { setJobs((await api.get(`/tenants/${tenantId}/imports`)).data.imports || []); }
    catch (e) { if (e?.response?.status !== 403) toast.error("Could not load imports"); }
    finally { setLoading(false); }
  }, [api, tenantId]);
  useEffect(() => { load(); }, [load]);

  const rollback = async (job) => {
    if (!window.confirm("Roll back this import? Records created only by this import will be removed; enriched people are preserved.")) return;
    try {
      const r = await api.post(`/tenants/${tenantId}/imports/${job.id}/rollback`);
      toast.success(`Rolled back — ${r.data.people_deleted} removed, ${r.data.people_kept_enriched} kept`);
      load();
    } catch { toast.error("Could not roll back"); }
  };

  if (!canManage) return <EmptyState icon={FileSpreadsheet} title="Imports"
    description="Your role doesn't include permission to import data." />;

  return (
    <div className="space-y-5" data-testid="imports-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Imports</h2>
          <p className="text-sm text-slate-500">Bring people in from a voter or contact file.</p>
        </div>
        <Button onClick={() => setWizard({ stage: "upload" })} data-testid="new-import-btn">
          <Upload className="mr-1.5 h-4 w-4" /> Import people
        </Button>
      </div>

      {loading ? <Loading label="Loading imports…" /> : jobs.length === 0 ? (
        <EmptyState icon={FileSpreadsheet} title="No imports yet"
          description="Upload a CSV to create or update people from a voter or contact file." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-left text-sm" data-testid="imports-table">
            <thead><tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2.5">File</th><th className="px-4 py-2.5">Rows</th>
              <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Result</th>
              <th className="px-4 py-2.5"></th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((j) => (
                <tr key={j.id} className="hover:bg-slate-50" data-testid={`import-row-${j.id}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{j.original_filename || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{j.row_count ?? "—"}</td>
                  <td className="px-4 py-2.5"><Badge variant={STATUS_VARIANT[j.status] || "neutral"}>{STATUS_LABEL[j.status] || j.status}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">
                    {j.commit_summary ? `${j.commit_summary.people_created || 0} new · ${j.commit_summary.matched || 0} matched` : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {["uploaded", "mapping", "ready_for_review"].includes(j.status) && (
                      <Button size="sm" variant="outline" data-testid={`resume-${j.id}`}
                        onClick={() => setWizard({ stage: j.status === "ready_for_review" ? "review" : "map", job: j })}>Continue</Button>
                    )}
                    {["validating", "committing"].includes(j.status) && (
                      <Button size="sm" variant="outline" data-testid={`view-progress-${j.id}`}
                        onClick={() => setWizard({ stage: j.status, job: j })}>View progress</Button>
                    )}
                    {j.status === "failed" && (
                      <Button size="sm" variant="outline" className="text-amber-700"
                        data-testid={`retry-${j.id}`} onClick={() => setWizard({ stage: "map", job: j })}>Review</Button>
                    )}
                    {j.status === "completed" && canRollback && (
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                        data-testid={`rollback-${j.id}`} onClick={() => rollback(j)}>
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Roll back
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {wizard && <ImportWizard api={api} tenantId={tenantId} canCommit={canCommit}
        initial={wizard} onClose={() => { setWizard(null); load(); }} />}
    </div>
  );
}

const PHASE = {
  validating: { label: "Validating and matching", verb: "reviewed" },
  committing: { label: "Importing people", verb: "complete" },
};

const ProgressPanel = ({ progress, phase }) => {
  const meta = PHASE[phase] || PHASE.validating;
  const pct = progress?.percent;
  const processed = progress?.processed ?? 0;
  const total = progress?.total ?? 0;
  return (
    <div className="space-y-4" data-testid="import-progress">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Loader2 className="h-4 w-4 animate-spin text-primary" /> {meta.label}
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
          <span data-testid="progress-count">
            {total ? `${processed.toLocaleString()} of ${total.toLocaleString()} rows ${meta.verb}`
                   : `${processed.toLocaleString()} rows ${meta.verb}`}
          </span>
          {pct != null && <span className="font-medium text-slate-700" data-testid="progress-percent">{pct}%</span>}
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-primary transition-all duration-500"
               style={{ width: `${pct != null ? pct : 6}%` }} />
        </div>
      </div>
      <div className="rounded-md bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-500" data-testid="leave-note">
        Your import is processing securely on our servers. You can leave this page and come back
        later — it won't stop, and refreshing won't restart it.
      </div>
    </div>
  );
};

const ImportWizard = ({ api, tenantId, canCommit, initial, onClose }) => {
  const [stage, setStage] = useState(initial.stage);
  const [job, setJob] = useState(initial.job || null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ source_type: "state_voter_file", source_name: "",
    jurisdiction: "", state: "", county: "", acquisition_date: "" });
  const [file, setFile] = useState(null);
  const [targets, setTargets] = useState([]);
  const [mapping, setMapping] = useState(job?.mapping || {});
  const [summary, setSummary] = useState(job?.validation_summary || null);
  const [progress, setProgress] = useState(null);
  const pollRef = useRef(null);
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const watch = useCallback((jid, kind) => {
    stopPoll();
    const tick = async () => {
      try {
        const r = await api.get(`/tenants/${tenantId}/imports/${jid}`);
        const jb = r.data.job; setProgress(r.data.progress);
        if (kind === "validating") {
          if (jb.status === "ready_for_review") { stopPoll(); setSummary(jb.validation_summary); setJob(jb); setStage("review"); setBusy(false); }
          else if (jb.status === "failed") { stopPoll(); setJob(jb); setStage("failed"); setBusy(false); }
        } else {
          if (jb.status === "completed") { stopPoll(); setJob(jb); setStage("done"); setBusy(false); }
          else if (jb.status === "failed") { stopPoll(); setJob(jb); setStage("failed"); setBusy(false); }
        }
      } catch { /* transient — keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 1500);
  }, [api, tenantId]);

  // Load mapping/summary for map/review; resume polling for in-progress jobs.
  useEffect(() => {
    if (job && (stage === "map" || stage === "review")) {
      api.get(`/tenants/${tenantId}/imports/${job.id}`).then((r) => {
        setTargets(r.data.target_fields || []);
        setMapping(r.data.job.mapping || {});
        setSummary(r.data.job.validation_summary || null);
      }).catch(() => {});
    }
    if (job && (initial.stage === "validating" || initial.stage === "committing") && !pollRef.current) {
      watch(job.id, initial.stage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, stage]);

  useEffect(() => () => stopPoll(), []);

  const doUpload = async () => {
    if (!file) { toast.error("Choose a CSV file first."); return; }
    if (!form.source_name.trim()) { toast.error("Enter a source name."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      const r = await api.post(`/tenants/${tenantId}/imports`, fd);
      setJob(r.data.job); setMapping(r.data.suggested_mapping || {});
      setTargets(r.data.target_fields || []); setStage("map");
    } catch (e) {
      const c = e?.response?.data?.detail;
      toast.error(c === "file_too_large" ? "That file is too large." :
        c === "too_many_rows" ? "That file has too many rows for now." :
        c === "not_utf8_csv" ? "Please upload a UTF-8 CSV file." :
        c === "no_data_rows" ? "That file has no data rows." : "Upload failed.");
    } finally { setBusy(false); }
  };

  const saveAndValidate = async () => {
    if (!Object.values(mapping).some((v) => v === "first_name" || v === "last_name")) {
      toast.error("Map at least a first or last name column."); return;
    }
    setBusy(true);
    try {
      await api.put(`/tenants/${tenantId}/imports/${job.id}/mapping`, { mapping });
      await api.post(`/tenants/${tenantId}/imports/${job.id}/validate`);
      setProgress(null); setStage("validating"); watch(job.id, "validating");
    } catch { toast.error("Could not start validation."); setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/imports/${job.id}/commit`);
      setProgress(null); setStage("committing"); watch(job.id, "committing");
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail === "already_committed" ? "Already imported." : "Commit failed.");
    }
  };

  const setMap = (col, val) => setMapping({ ...mapping, [col]: val || null });

  const titles = { upload: "Upload file", map: "Map fields", validating: "Validating & matching",
    review: "Review matches", committing: "Importing people", done: "Import complete",
    failed: "Import needs attention" };
  const descs = {
    upload: "Choose a CSV and describe where it came from. You're responsible for having lawful authority to use this data.",
    map: "Tell us which column is which. Unmapped columns are kept as source data.",
    validating: "We're checking your file and matching it against existing people.",
    review: "Here's what will happen when you import.",
    committing: "We're bringing your people in. This can take a while for large files.",
    done: "", failed: "" };

  const footer =
    stage === "upload" ? <><Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={doUpload} disabled={busy} data-testid="wizard-upload-next">{busy ? "Uploading…" : "Continue"} <ArrowRight className="ml-1 h-4 w-4" /></Button></> :
    stage === "map" ? <><Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={saveAndValidate} disabled={busy} data-testid="wizard-validate">{busy ? "Starting…" : "Validate"} <ArrowRight className="ml-1 h-4 w-4" /></Button></> :
    (stage === "validating" || stage === "committing") ?
      <Button variant="ghost" onClick={() => { stopPoll(); onClose(); }} data-testid="wizard-background-close">Close — keep processing in background</Button> :
    stage === "review" ? <><Button variant="ghost" onClick={onClose}>Close</Button>
      {canCommit && <Button onClick={commit} disabled={busy} data-testid="wizard-commit">{busy ? "Starting…" : "Ready to import"}</Button>}</> :
    stage === "failed" ? <><Button variant="ghost" onClick={onClose}>Close</Button>
      <Button onClick={() => setStage("map")} data-testid="wizard-retry">Try again</Button></> :
      <Button onClick={onClose} data-testid="wizard-done">Done</Button>;

  return (
    <Modal open onClose={onClose} size="lg" testid="import-wizard"
      title={titles[stage]} description={descs[stage]} footer={footer}>
      {stage === "upload" && (
        <div className="space-y-4">
          <Field label="CSV file">
            <input type="file" accept=".csv,text/csv" data-testid="import-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-accent file:px-3 file:py-1.5 file:text-primary" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Source type"><Select value={form.source_type} data-testid="import-source-type"
              onChange={(e) => setForm({ ...form, source_type: e.target.value })}>
              {SOURCE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
            <Field label="Source name"><TextInput value={form.source_name} data-testid="import-source-name"
              onChange={(e) => setForm({ ...form, source_name: e.target.value })} placeholder="e.g. PA Dept. of State" /></Field>
            <Field label="Jurisdiction"><TextInput value={form.jurisdiction}
              onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} placeholder="e.g. Erie County, PA" /></Field>
            <Field label="As-of / acquisition date"><TextInput value={form.acquisition_date}
              onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })} placeholder="YYYY-MM-DD" /></Field>
          </div>
        </div>
      )}
      {stage === "map" && (
        <div className="max-h-[50vh] space-y-2 overflow-y-auto" data-testid="mapping-grid">
          {(job?.columns || Object.keys(mapping)).map((col) => (
            <div key={col} className="flex items-center gap-3">
              <span className="w-1/2 truncate text-sm font-medium text-slate-700" title={col}>{col}</span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
              <Select value={mapping[col] || ""} data-testid={`map-${col}`}
                onChange={(e) => setMap(col, e.target.value)} className="w-1/2">
                <option value="">— Keep as source only —</option>
                {targets.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </Select>
            </div>
          ))}
        </div>
      )}
      {(stage === "validating" || stage === "committing") && (
        <ProgressPanel progress={progress} phase={stage} />
      )}
      {stage === "review" && summary && (
        <div className="space-y-2" data-testid="review-summary">
          <SummaryRow label="Rows processed" value={summary.total} />
          <SummaryRow label="New people proposed" value={summary.create || 0} />
          <SummaryRow label="Existing people matched" value={summary.attach || 0} />
          <SummaryRow label="Possible matches (need review)" value={summary.review || 0} warn />
          <SummaryRow label="Invalid rows" value={summary.invalid || 0} warn />
          <p className="pt-2 text-xs text-slate-400">Possible matches and invalid rows are held and not imported. Imported contact info never creates messaging consent.</p>
        </div>
      )}
      {stage === "done" && job?.commit_summary && (
        <div className="space-y-3 text-center" data-testid="done-summary">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
          <p className="text-sm text-slate-700">
            {job.commit_summary.people_created} people created · {job.commit_summary.matched} matched ·
            {" "}{job.commit_summary.voter_records} source records saved
          </p>
          {job.commit_summary.skipped > 0 && <p className="text-xs text-slate-400">{job.commit_summary.skipped} rows skipped (held for review or invalid).</p>}
        </div>
      )}
      {stage === "failed" && (
        <div className="space-y-3 text-center" data-testid="failed-summary">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
          <p className="text-sm font-semibold text-slate-800">Import needs attention</p>
          <p className="text-sm text-slate-600">Something went wrong while processing this file. You can adjust the column mapping and try again. Your existing data was not changed.</p>
        </div>
      )}
    </Modal>
  );
};

const SummaryRow = ({ label, value, warn }) => (
  <div className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-sm">
    <span className="text-slate-600">{label}</span>
    <Badge variant={warn && value > 0 ? "warning" : "neutral"}>{value}</Badge>
  </div>
);
